'use client';

import { useState, useRef, useCallback, useLayoutEffect, useMemo, type ReactNode } from 'react';
import { createExternalStore } from '@/shared/libs/create-external-store';
import { FullscreenStoreContext, FullscreenActionsContext, type FullscreenState } from './fullscreen-context';

export const FullscreenProvider = ({ children }: { children: ReactNode }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [needFullscreenButton, setNeedFullscreenButton] = useState(false);
  const [headerEnabled, setHeaderEnabled] = useState(true);
  const [handler, setHandler] = useState<(() => void) | null>(null);

  const storeRef = useRef<ReturnType<typeof createExternalStore<FullscreenState>> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createExternalStore<FullscreenState>({
      isFullscreen,
      needFullscreenButton,
      headerEnabled,
      handler,
    });
  }

  // useLayoutEffect — иначе useIsFullscreen()/useHeaderEnabled() на кадр отставали бы
  useLayoutEffect(() => {
    storeRef.current!.setState({ isFullscreen, needFullscreenButton, headerEnabled, handler });
  }, [isFullscreen, needFullscreenButton, headerEnabled, handler]);

  const registerClickHandler = useCallback((fn: () => void) => {
    setHandler(() => fn);
  }, []);

  const actions = useMemo(
    () => ({
      setIsFullscreen,
      setNeedFullscreenButton,
      setHeaderEnabled,
      registerClickHandler,
    }),
    [registerClickHandler]
  );

  return (
    <FullscreenStoreContext.Provider value={storeRef.current}>
      <FullscreenActionsContext.Provider value={actions}>
        {children}
      </FullscreenActionsContext.Provider>
    </FullscreenStoreContext.Provider>
  );
};
